import React, { useState } from "react";
import { Modal, Form, InputNumber, Checkbox, Button, Space, Alert, Divider, Typography, Select } from 'antd';
import { PrinterOutlined, BarcodeOutlined, QrcodeOutlined } from "@ant-design/icons";
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

const { Text } = Typography;
const { Option } = Select;

const BarcodeSheetGenerator = ({ visible, onClose }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Generate barcode as base64 data URL
    const generateBarcodeDataUrl = (text) => {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, text, {
            format: 'CODE128',
            width: 2,
            height: 50,
            displayValue: true,
            fontSize: 12,
            margin: 5,
            textMargin: 2
        });
        return canvas.toDataURL('image/png');
    };

    // Generate QR code as base64 data URL
    const generateQRCodeDataUrl = async (text) => {
        try {
            return await QRCode.toDataURL(text, {
                width: 100,
                margin: 1,
                errorCorrectionLevel: 'M'
            });
        } catch (err) {
            console.error('QR Code generation error:', err);
            return null;
        }
    };

    // Generate printable HTML content
    const generatePrintableHTML = async (codes, includeBarcode, includeQrcode) => {
        const pageWidth = 190; // mm
        const pageHeight = 277; // mm

        let htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Asset Codes Sheet</title>
    <style>
        @page {
            size: A4;
            margin: 10mm;
        }
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: Arial, sans-serif;
            font-size: 10px;
        }
        .page {
            width: ${pageWidth}mm;
            height: ${pageHeight}mm;
            page-break-after: always;
            position: relative;
        }
        .page:last-child {
            page-break-after: auto;
        }
        .barcode-container {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 2mm;
            padding: 2mm;
        }
        .barcode-label {
            width: 100%;
            height: 25mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            border: 1px dashed #ccc;
        }
        .barcode-label img {
            max-width: 58mm;
            max-height: 20mm;
        }
        .qrcode-container {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 2mm;
            padding: 2mm;
        }
        .qrcode-label {
            width: 100%;
            height: 42mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            border: 1px dashed #ccc;
        }
        .qrcode-label img {
            width: 30mm;
            height: 30mm;
        }
        .qrcode-label .code-text {
            font-size: 7px;
            margin-top: 2mm;
            text-align: center;
            word-break: break-all;
        }
        @media print {
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            .page {
                margin: 0;
            }
            .barcode-label, .qrcode-label {
                border: 1px dashed #ddd;
            }
        }
    </style>
</head>
<body>`;

        // Generate barcode pages
        if (includeBarcode) {
            const labelsPerPage = 30; // 3 columns x 10 rows
            const pages = Math.ceil(codes.length / labelsPerPage);

            for (let page = 0; page < pages; page++) {
                htmlContent += `<div class="page"><div class="barcode-container">`;
                const startIdx = page * labelsPerPage;
                const endIdx = Math.min(startIdx + labelsPerPage, codes.length);

                for (let i = startIdx; i < endIdx; i++) {
                    const code = codes[i];
                    const barcodeDataUrl = generateBarcodeDataUrl(code);
                    htmlContent += `
                        <div class="barcode-label">
                            <img src="${barcodeDataUrl}" alt="${code}">
                        </div>`;
                }

                htmlContent += `</div></div>`;
            }
        }

        // Generate QR code pages
        if (includeQrcode) {
            const labelsPerPage = 35; // 5 columns x 7 rows
            const pages = Math.ceil(codes.length / labelsPerPage);

            for (let page = 0; page < pages; page++) {
                htmlContent += `<div class="page"><div class="qrcode-container">`;
                const startIdx = page * labelsPerPage;
                const endIdx = Math.min(startIdx + labelsPerPage, codes.length);

                for (let i = startIdx; i < endIdx; i++) {
                    const code = codes[i];
                    const qrDataUrl = await generateQRCodeDataUrl(code);
                    htmlContent += `
                        <div class="qrcode-label">
                            <img src="${qrDataUrl}" alt="${code}">
                            <div class="code-text">${code}</div>
                        </div>`;
                }

                htmlContent += `</div></div>`;
            }
        }

        htmlContent += `
</body>
</html>`;

        return htmlContent;
    };

    const handleGenerate = async () => {
        try {
            const values = await form.validateFields();

            if (!values.includeBarcode && !values.includeQrcode) {
                return;
            }

            setLoading(true);

            // Generate code values
            const year = values.year;
            const month = values.month;
            const startNum = values.startNumber;
            const endNum = values.endNumber;
            const repeatCount = values.repeatCount || 1;

            const codes = [];
            for (let num = startNum; num <= endNum; num++) {
                const codeValue = `TESONLLP-AST${year}${month}${String(num).padStart(4, '0')}`;
                for (let r = 0; r < repeatCount; r++) {
                    codes.push(codeValue);
                }
            }

            // Generate HTML content
            const htmlContent = await generatePrintableHTML(
                codes,
                values.includeBarcode,
                values.includeQrcode
            );

            // Open in new window and print
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write(htmlContent);
                printWindow.document.close();

                // Wait for images to load before printing
                printWindow.onload = () => {
                    setTimeout(() => {
                        printWindow.print();
                    }, 500);
                };
            } else {
                alert('Please allow popups for this site to print the barcode sheet.');
            }

            setLoading(false);
        } catch (error) {
            console.error('Generation failed:', error);
            setLoading(false);
        }
    };

    const handleReset = () => {
        form.resetFields();
    };

    const calculateCount = () => {
        const values = form.getFieldsValue();
        if (values.startNumber && values.endNumber) {
            const uniqueCodes = values.endNumber - values.startNumber + 1;
            const repeat = values.repeatCount || 1;
            const totalLabels = uniqueCodes * repeat;
            return {
                uniqueCodes: uniqueCodes > 0 ? uniqueCodes : 0,
                repeatCount: repeat,
                totalLabels: totalLabels > 0 ? totalLabels : 0
            };
        }
        return { uniqueCodes: 0, repeatCount: 1, totalLabels: 0 };
    };

    return (
        <Modal
            title={
                <Space>
                    <BarcodeOutlined />
                    <span>Generate Barcode / QR Code Sheet</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={500}
            destroyOnClose
        >
            <Alert
                message="Generate printable barcode or QR code sheets for A4 paper"
                description="Enter a number range and select the code type. The generated sheet will be optimized for printing with equidistant labels for easy cutting."
                type="info"
                showIcon
                style={{ marginBottom: 20 }}
            />

            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    year: new Date().getFullYear(),
                    month: String(new Date().getMonth() + 1).padStart(2, '0'),
                    startNumber: 1,
                    endNumber: 30,
                    repeatCount: 1,
                    includeBarcode: true,
                    includeQrcode: false
                }}
                onValuesChange={() => {
                    // Force re-render to update count display
                    form.validateFields(['startNumber', 'endNumber', 'repeatCount']).catch(() => {});
                }}
            >
                <Alert
                    message={
                        <span>
                            <strong>Format:</strong> TESONLLP-AST&lt;YYYY&gt;&lt;MM&gt;&lt;NNNN&gt;
                            <br />
                            <strong>Example:</strong> TESONLLP-AST2025110001
                        </span>
                    }
                    type="info"
                    style={{ marginBottom: 16 }}
                />

                <Divider orientation="left" plain>Year & Month</Divider>

                <Space style={{ width: '100%' }} align="start">
                    <Form.Item
                        name="year"
                        label="Year"
                        rules={[
                            { required: true, message: 'Year is required' },
                            { type: 'number', min: 2020, max: 2099, message: 'Year must be 2020-2099' }
                        ]}
                    >
                        <InputNumber
                            min={2020}
                            max={2099}
                            style={{ width: 100 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="month"
                        label="Month"
                        rules={[{ required: true, message: 'Month is required' }]}
                    >
                        <Select style={{ width: 150 }}>
                            <Option value="01">01 - January</Option>
                            <Option value="02">02 - February</Option>
                            <Option value="03">03 - March</Option>
                            <Option value="04">04 - April</Option>
                            <Option value="05">05 - May</Option>
                            <Option value="06">06 - June</Option>
                            <Option value="07">07 - July</Option>
                            <Option value="08">08 - August</Option>
                            <Option value="09">09 - September</Option>
                            <Option value="10">10 - October</Option>
                            <Option value="11">11 - November</Option>
                            <Option value="12">12 - December</Option>
                        </Select>
                    </Form.Item>
                </Space>

                <Divider orientation="left" plain>Number Range (1-9999)</Divider>

                <Space style={{ width: '100%' }} align="start">
                    <Form.Item
                        name="startNumber"
                        label="Start Number"
                        rules={[
                            { required: true, message: 'Start number is required' },
                            { type: 'number', min: 1, max: 9999, message: 'Must be 1-9999' }
                        ]}
                    >
                        <InputNumber
                            min={1}
                            max={9999}
                            style={{ width: 100 }}
                            placeholder="1"
                        />
                    </Form.Item>

                    <Form.Item
                        name="endNumber"
                        label="End Number"
                        rules={[
                            { required: true, message: 'End number is required' },
                            { type: 'number', min: 1, max: 9999, message: 'Must be 1-9999' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('startNumber') <= value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('End must be >= start'));
                                },
                            }),
                        ]}
                    >
                        <InputNumber
                            min={1}
                            max={9999}
                            style={{ width: 100 }}
                            placeholder="30"
                        />
                    </Form.Item>

                    <Form.Item
                        name="repeatCount"
                        label="Copies Each"
                        tooltip="Number of copies to print for each code"
                        rules={[
                            { type: 'number', min: 1, max: 10, message: 'Must be 1-10' }
                        ]}
                    >
                        <InputNumber
                            min={1}
                            max={10}
                            style={{ width: 100 }}
                            placeholder="1"
                        />
                    </Form.Item>
                </Space>

                <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
                    <Text type="secondary">
                        Unique codes: <Text strong>{calculateCount().uniqueCodes}</Text>
                        {' | '}
                        Copies each: <Text strong>{calculateCount().repeatCount}</Text>
                        {' | '}
                        Total labels: <Text strong>{calculateCount().totalLabels}</Text>
                    </Text>
                </div>

                <Divider orientation="left" plain>Code Type</Divider>

                <Form.Item
                    name="includeBarcode"
                    valuePropName="checked"
                    style={{ marginBottom: 8 }}
                >
                    <Checkbox>
                        <Space>
                            <BarcodeOutlined />
                            <span>Generate Barcodes</span>
                            <Text type="secondary">(3 x 10 = 30 per page)</Text>
                        </Space>
                    </Checkbox>
                </Form.Item>

                <Form.Item
                    name="includeQrcode"
                    valuePropName="checked"
                    style={{ marginBottom: 16 }}
                >
                    <Checkbox>
                        <Space>
                            <QrcodeOutlined />
                            <span>Generate QR Codes</span>
                            <Text type="secondary">(5 x 7 = 35 per page)</Text>
                        </Space>
                    </Checkbox>
                </Form.Item>

                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                        prevValues.includeBarcode !== currentValues.includeBarcode ||
                        prevValues.includeQrcode !== currentValues.includeQrcode
                    }
                >
                    {({ getFieldValue }) => {
                        const includeBarcode = getFieldValue('includeBarcode');
                        const includeQrcode = getFieldValue('includeQrcode');

                        if (!includeBarcode && !includeQrcode) {
                            return (
                                <Alert
                                    message="Please select at least one code type"
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            );
                        }

                        if (includeBarcode && includeQrcode) {
                            return (
                                <Alert
                                    message="Barcodes and QR codes will be generated on separate sheets for optimal layout and easy cutting"
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            );
                        }

                        return null;
                    }}
                </Form.Item>

                <Divider />

                <Form.Item style={{ marginBottom: 0 }}>
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={handleReset}>
                            Reset
                        </Button>
                        <Button onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            icon={<PrinterOutlined />}
                            onClick={handleGenerate}
                            loading={loading}
                        >
                            Generate & Print
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default BarcodeSheetGenerator;
